import { useMemo, useCallback } from "react";
import avatar from "assets/img/avatars/avatar1.png";

/**
 * Hook personnalisé pour gérer les URLs des photos
 * @returns {Object} Méthodes pour manipuler les URLs de photos
 */
export const usePhotoUrl = () => {
  // En React, on accède généralement aux variables d'environnement avec process.env
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  /**
   * Détermine si une URL d'image est une URL absolue (http/https)
   * @param {string} url L'URL à vérifier
   * @returns {boolean} indiquant si c'est une URL absolue
   */
  const isAbsoluteUrl = useCallback((url) => {
    return url?.startsWith("http://") || url?.startsWith("https://");
  }, []);

  /**
   * Retourne l'URL de base de l'API
   * @returns {string} L'URL de base de l'API
   */
  const getApiBaseUrl = useCallback(() => {
    return apiBaseUrl;
  }, [apiBaseUrl]);

  /**
   * Construit l'URL complète pour une photo basée sur le chemin stocké
   * @param {string} photoPath Le chemin de la photo tel que stocké dans la base de données
   * @param {string} defaultImagePath Image par défaut à utiliser si photoPath est vide (optionnel)
   * @returns {string} L'URL complète de l'image
   */
  const getPhotoUrl = useCallback(
    (photoPath, defaultImagePath = avatar) => {
      if (!photoPath) {
        return defaultImagePath; // Image par défaut
      }

      // Si le chemin commence déjà par http, c'est une URL complète
      if (photoPath.startsWith("http")) {
        return photoPath;
      }

      // Si le chemin commence par un slash, ne pas en ajouter un autre
      if (photoPath.startsWith("/")) {
        return `${apiBaseUrl}${photoPath}`;
      }

      if (photoPath.startsWith("assets")) {
        return `${apiBaseUrl}/${photoPath}`;
      }

      // Sinon ajouter un slash entre l'URL de base et le chemin de la photo
      return `${apiBaseUrl}/image/${photoPath}`;
    },
    [apiBaseUrl]
  );

  // Utilisation de useMemo pour éviter de recréer l'objet à chaque rendu
  return useMemo(
    () => ({
      getPhotoUrl,
      isAbsoluteUrl,
      getApiBaseUrl,
    }),
    [getPhotoUrl, isAbsoluteUrl, getApiBaseUrl]
  );
};